import { COLLECTOR_STATUSES } from './types'

import type { CollectorWithSources } from './types'

/**
 * The filter row's vocabulary: every Collector status, plus the unfiltered view.
 *
 * Spread from {@link COLLECTOR_STATUSES} rather than re-typed, so a new status
 * gains its filter for free and the two lists cannot fall out of step.
 */
export const COLLECTOR_STATUS_FILTERS = ['all', ...COLLECTOR_STATUSES] as const

export type CollectorStatusFilter = (typeof COLLECTOR_STATUS_FILTERS)[number]

const DEFAULT_COLLECTOR_STATUS_FILTER: CollectorStatusFilter = 'all'

/** Narrow a raw search-param string to the vocabulary. */
export function isCollectorStatusFilter(
	value: string | undefined,
): value is CollectorStatusFilter {
	return (
		!!value &&
		(COLLECTOR_STATUS_FILTERS as ReadonlyArray<string>).includes(value)
	)
}

/**
 * What the URL means, at read time.
 *
 * The search param carries no parse-time default: an absent `?status=` and an
 * explicit `?status=all` must stay distinguishable in the URL, and the fallback
 * belongs wherever search is turned into meaning (P-22 §1). An unrecognised
 * value degrades to the unfiltered view rather than an empty table — a stale
 * link should show too much, never nothing.
 */
export function resolveCollectorStatusFilter(
	value: string | undefined,
): CollectorStatusFilter {
	return isCollectorStatusFilter(value)
		? value
		: DEFAULT_COLLECTOR_STATUS_FILTER
}

/**
 * How many Collectors each filter would show, for the row's count badges.
 *
 * Every filter gets an entry even at zero, so a status the tenant currently has
 * none of still renders as an (empty) option — a filter row whose options come
 * and go is one a user cannot learn. Statuses outside the vocabulary are counted
 * in `all` only: they have no option to sit under, and inventing a key here
 * would render a filter nothing else in the app knows about.
 */
export function countCollectorsByStatusFilter(
	collectors: ReadonlyArray<CollectorWithSources>,
): Record<CollectorStatusFilter, number> {
	const counts: Record<CollectorStatusFilter, number> = {
		all: collectors.length,
		connected: 0,
		degraded: 0,
		offline: 0,
	}

	for (const collector of collectors) {
		if (isCollectorStatusFilter(collector.status)) {
			counts[collector.status] += 1
		}
	}

	return counts
}

/** Rows the current filter admits. `all` admits everything. */
export function filterCollectorsByStatus(
	collectors: ReadonlyArray<CollectorWithSources>,
	filter: CollectorStatusFilter,
): CollectorWithSources[] {
	if (filter === 'all') return [...collectors]
	return collectors.filter((collector) => collector.status === filter)
}
